import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import isPropValid from '@emotion/is-prop-valid';

const GROW_BASE = 999;

// FormItem
export const FormItem = styled(
  React.forwardRef(({ component: Component = 'div', empty, size = '1', ...rest }, ref) => (
    <Component ref={ref} {...rest} />
  ))
)(({ size, empty }) => ({
  flex: size === '0' ? '1 0 auto' : `${GROW_BASE * parseFloat(size)} 1 15%`,
  minWidth: size === '0' ? undefined : '300px !important',
  ...(empty && {
    marginTop: 'auto',
    marginBottom: 'auto',
    minHeight: 1
  })
}));

FormItem.defaultProps = {
  component: 'div',
  size: '1'
};

FormItem.propTypes = {
  component: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node, PropTypes.object]),
  empty: PropTypes.bool,
  size: PropTypes.string
};

// FormSection
export const FormSection = styled(
  React.forwardRef(
    (
      {
        component: Component = 'div',
        divider,
        padding,
        widthPercent = 100,
        wrap = 'wrap',
        ...rest
      },
      ref
    ) => {
      // ✅ Filter out invalid DOM props
      const safeRest = Object.fromEntries(Object.entries(rest).filter(([key]) => isPropValid(key)));

      return <Component ref={ref} {...safeRest} />;
    }
  )
)(({ theme, divider, padding, widthPercent, wrap }) => ({
  boxSizing: 'border-box',
  border: `0 solid ${theme.color.grey[500]}`,
  display: 'flex',
  flexWrap: wrap || 'wrap',
  flex: `1 1 ${widthPercent || 100}%`,
  ...(padding && { padding: theme.spacing(...String(padding).split(' ')) }),
  ...(divider &&
    (divider === 'bottom'
      ? { borderBottomWidth: 1 }
      : divider === 'top'
      ? { borderTopWidth: 1 }
      : { borderWidth: '1px 0' }))
}));

// FormRow
export const FormRow = styled(FormSection)(({ theme }) => ({
  margin: `0 ${theme.spacing(-1)}`,
  width: `calc(100% + ${theme.spacing(2)})`,
  [`& ${FormItem}`]: {
    margin: theme.spacing(1.5, 1)
  }
}));

// FormContainer
export const FormContainer = styled('form')(({ ml }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  marginLeft: ml ? `${ml}px` : '0px'
}));

// FormTextItem
export const FormTextItem = styled(FormItem)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(1),
  marginBottom: 0
}));
